import React, {useEffect} from "react";
import {FullBotUi, UpdateWidgetAppearanceSettingsRequest} from "../../../../../api/generated";
import {WidgetPreview} from "./components/WidgetPreview/WidgetPreview";
import {WidgetAppearanceForm} from "./components/AppearanceSettingForm/WidgetAppearanceForm";
import {FeedbackSettings} from "./components/FeedbackSettings/FeedbackSettings";
import styles from "./WidgetAppearanceSettingsTab.module.css";
import {useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
    feedbackSettingsFromBot,
    fetchDefaultWidgetAppearanceSettings,
    selectDefaultSettingsLoadingStatus,
    selectHasUnsavedChanges,
    selectHasUnsavedFeedbackChanges,
    selectSelectedLocaleVariant,
    selectUpdateFeedbackStatus,
    selectUpdateStatus,
    setFeedbackSettings,
    setLocaleContext,
    updateFeedbackSettingsOfBot,
    updateWidgetAppearanceSettings
} from "./widgetAppearanceUpdateSlice";
import {FullWidgetAppearanceSettingUiWithVariants} from "./widgetAppearanceUpdateSlice";
import { useUnsavedChangesWarning } from "../../../../../hooks/useUnsavedChangesWarning";
import {FormContext, ValidatedForm} from "../../../../elements/validator/validatedForm/ValidatedForm";
import {AppearanceTabSection} from "../../../../elements/appearanceTabSection/AppearanceTabSection";
import {Button, ButtonState} from "../../../../elements/button/Button";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {
    showErrorToastNotification,
    showSuccessToastNotification
} from "../../../../elements/toastNotification/toastNotificationSlice";
import {fetchBotById} from "../../botSlice";

// The platform caches the settings it serves to the widget for a minute (widget.settings.response-cache-ttl-ms)
const WIDGET_CACHE_NOTICE = "Saved changes can take up to 1 minute to appear in the live widget.";

interface WidgetAppearanceSettingsTabProps {
    widgetAppearanceSettings: FullWidgetAppearanceSettingUiWithVariants
    bot: FullBotUi
}

export const WidgetAppearanceSettingsTab: React.FC<WidgetAppearanceSettingsTabProps> = ({widgetAppearanceSettings, bot}) => {
    const dispatch = useAppDispatch();
    const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);
    const hasUnsavedFeedbackChanges = useAppSelector(selectHasUnsavedFeedbackChanges);
    const selectedLocaleVariant = useAppSelector(selectSelectedLocaleVariant);
    const updateStatus = useAppSelector(selectUpdateStatus);
    const updateFeedbackStatus = useAppSelector(selectUpdateFeedbackStatus);
    const defaultSettingsStatus = useAppSelector(selectDefaultSettingsLoadingStatus);
    const {publicBotId} = widgetAppearanceSettings;
    const {botType} = bot;

    useUnsavedChangesWarning(hasUnsavedChanges);

    useEffect(() => {
        dispatch(setLocaleContext(bot.defaultLocale));
    }, [bot.defaultLocale, dispatch]);

    useEffect(() => {
        dispatch(setFeedbackSettings(feedbackSettingsFromBot(bot)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bot.publicId, bot.surveyAfterMessagesEnabled, bot.surveyAfterMessagesCount, bot.surveyAfterBookingEnabled, dispatch]);

    if (!publicBotId) {
        return <div>Missing publicBotId for Widget Appearance settings</div>;
    }

    const handleSave = async () => {
        try {
            await dispatch(updateWidgetAppearanceSettings(publicBotId)).unwrap();
        } catch (error) {
            dispatch(showErrorToastNotification({
                message: typeof error === "string" && error ? error : "Failed to update widget appearance settings"
            }));
            return;
        }

        if (hasUnsavedFeedbackChanges) {
            try {
                await dispatch(updateFeedbackSettingsOfBot(publicBotId)).unwrap();
                dispatch(fetchBotById(publicBotId));
            } catch (error) {
                dispatch(showErrorToastNotification({
                    message: typeof error === "string" && error ? error : "Failed to update feedback settings"
                }));
                return;
            }
        }

        dispatch(showSuccessToastNotification({
            message: `Widget appearance settings updated successfully. ${WIDGET_CACHE_NOTICE}`
        }));
    };

    const handleRevertToDefault = () => {
        if (!botType) {
            console.error("BotType is required to fetch default widget appearance settings");
            return;
        }
        dispatch(fetchDefaultWidgetAppearanceSettings(botType));
    };

    const isSaving = updateStatus === RequestStatus.PENDING || updateFeedbackStatus === RequestStatus.PENDING;

    return <ValidatedForm<UpdateWidgetAppearanceSettingsRequest> submitForm={handleSave}>
        {(formCtx: FormContext<UpdateWidgetAppearanceSettingsRequest>) => {
            const {updateValidationStatus, getMessage, formDisabled, onSubmitClick, removeErrorByFieldName} = formCtx;

            const getMessageWrapper = (f: string): string => {
                return getMessage(f as keyof UpdateWidgetAppearanceSettingsRequest) || '';
            };

            return <div className={styles.wrapper}>
                <div className={styles.toolbar}>
                    <p className={styles.cacheNotice}>{WIDGET_CACHE_NOTICE}</p>
                    <div className={styles.buttonContainer}>
                        <Button
                            title="Revert to Default"
                            state={defaultSettingsStatus === RequestStatus.PENDING
                                ? ButtonState.DISABLED
                                : ButtonState.SECONDARY}
                            handleClick={handleRevertToDefault}
                        />
                        <Button
                            title="Save Changes"
                            state={formDisabled || isSaving
                                ? ButtonState.DISABLED
                                : ButtonState.PRIMARY}
                            handleClick={onSubmitClick}
                        />
                    </div>
                </div>
                <div className={styles.columns}>
                    <div className={styles.settingsColumn}>
                        <AppearanceTabSection title="Appearance" collapsible>
                            <WidgetAppearanceForm
                                botType={botType}
                                getMessageWrapper={getMessageWrapper}
                                updateValidationStatus={updateValidationStatus}
                            />
                        </AppearanceTabSection>
                        <AppearanceTabSection title="Feedback" collapsible>
                            <FeedbackSettings
                                getMessageWrapper={getMessageWrapper}
                                updateValidationStatus={updateValidationStatus}
                                removeErrorByFieldName={removeErrorByFieldName}
                            />
                        </AppearanceTabSection>
                    </div>
                    <div className={styles.previewColumn}>
                        <WidgetPreview botType={botType} settings={selectedLocaleVariant}/>
                    </div>
                </div>
            </div>;
        }}
    </ValidatedForm>
}
